import React, { useEffect, useRef, useState } from 'react';
import { get, isEmpty, isString } from 'lodash';

import Spinner from '../Spinner';
import { generateMediaUrl } from './Url';
import style from './style.module.css';

const BrokenImage = ({ className, icon = "fa-image", sizer }) => {
    return (
        <>
            {
                sizer === true && (
                    <div className={style.sizer}></div>
                )
            }
            <div className={style.bsz}>
                <div className={`${style.bgImage}${className ? " " + className : ""}`}>
                    <div className={style.fa}>
                        <i className={`fa-solid ${icon}`}></i>
                    </div>
                </div>
            </div>
        </>
    )
}

const NormalImage = ({ src, size, className, alt, cPosition, showCaption }) => {
    const srcUrl = GetUrl(src, size);
    let caption = get(src, "caption", "");
    return (
        <div className={style.bsz}>
            <div className={`${style.bgImage}${className ? " " + className : ""}`} style={{ backgroundImage: `url(${srcUrl})` }}>
                <img src={srcUrl} alt={alt} />
            </div>
            {
                showCaption === true && caption && (
                    <div className={`${style.captionWrap}${style[cPosition] ? ` ${style[cPosition]}` : ""}`}>
                        <p title={caption} className={`${style.caption}`}>{caption}</p>
                    </div>
                )
            }
        </div>
    )
}

const LazyImage = ({ src, className, alt, sizer, defaultSize = 50, size, callBack, isLoad = false, cPosition, showCaption }) => {
    let srcUrl = GetUrl(src, size);
    let caption = get(src, "caption", "");

    const [loaded, setLoaded] = useState(0);
    const [dimesion, setDimension] = useState([0, 0]);
    const [isIntersecting, setIntersecting] = useState(false)
    const ref = useRef();
    let img = new Image();
    const observer = new IntersectionObserver(
        ([entry]) => setIntersecting(entry.isIntersecting)
    )

    const loadImage = () => {
        img.src = srcUrl;
        if (img.complete || img.readystate === 4) {
            setLoaded(1);
            setDimension([img.width, img.height]);
            if (isLoad !== false && typeof isLoad === 'function') {
                isLoad(true);
            }
        }
        else {
            img.onload = () => {
                setLoaded(1);
                setDimension([img.width, img.height]);
                if (isLoad !== false && typeof isLoad === 'function') {
                    isLoad(true);
                }
            }
            img.onerror = () => {
                setLoaded(2);
            }
        }
        if (callBack !== false && typeof callBack === 'function') {
            const { innerWidth, innerHeight } = window;
            setTimeout(() => {
                let width = innerWidth;
                let height = img.height * width / img.width;

                if (height > innerHeight) {
                    height = innerHeight;
                    width = img.width * height / img.height;
                }
                callBack(width, height);
            }, 100);
        }
    }

    useEffect(() => {
        if (loaded === 0) {
            if (isIntersecting) {
                // Remove the observer once intersected or error
                observer.disconnect();
                loadImage();
            }
            else {
                observer.observe(ref.current);
            }
        }
        // Remove the observer as soon as the component is unmounted
        return () => { observer.disconnect() }
    })

    return (
        <>
            {
                sizer === true && (
                    <div className={style.sizer} style={{ paddingTop: `${dimesion[0] > 0 && dimesion[1] > 0 ? dimesion[1] * 100 / dimesion[0] : defaultSize}%` }}></div>
                )
            }
            <div className={style.bsz} ref={ref}>
                {
                    !isEmpty(src.blurred) && (
                        <div className={style.bgImage} style={{ backgroundImage: `url(data:image/${src.extension};base64,${src.blurred})` }}></div>
                    )
                }
                <div className={`${style.bgImage}${className ? " " + className : ""}${loaded === 0 ? " " + style.loading : ""}`} style={{ backgroundImage: loaded === 1 ? `url("${encodeURI(srcUrl)}")` : "none" }}>
                    {
                        loaded === 1 && (
                            <>
                                <img src={srcUrl} alt={alt} />
                            </>
                        )
                    }
                    {
                        loaded === 0 && isEmpty(src.blurred) && (
                            <div className={style.spinner}>
                                <Spinner color="#fff" />
                            </div>
                        )
                    }
                    {
                        loaded === 2 && (
                            <div className={style.fa}>
                                <i className={`fa-regular fa-image-slash`}></i>
                            </div>
                        )
                    }
                </div>
                {
                    showCaption === true && caption && loaded === 1 && (
                        <div className={`${style.captionWrap}${style[cPosition] ? ` ${style[cPosition]}` : ""}`}>
                            <p title={caption} className={`${style.caption}`}>{caption}</p>
                        </div>
                    )
                }
            </div>
        </>
    )
}

const GetUrl = (src, size) => {
    if (isString(src)) {
        return src;
    }

    return generateMediaUrl(src, size);
}

const GetLazy = (lazyProp) => {
    const query = new URLSearchParams(window.location.search);
    const lazy = query.get("lazy"); 
    if (lazy === "false") {
        return false;
    }
    return lazyProp;
}

const Background = ({ src, size, className = false, alt = "", lazy: lazyProp = true, fallback = true, retainAspectRatio = false, callBack = false, isLoad = false, defaultSize, cPosition = "topLeft", showCaption = false }) => {
    // Switch off lazy if specified in url
    const lazy = GetLazy(lazyProp);

    //If src is empty then display a broken image
    if (!src) {
        if (fallback === true) {
            //Show a broken image for fallback if set to true
            return (
                <BrokenImage sizer={retainAspectRatio} />
            )
        }
        return null;
    }

    //If lazy load is disabled, then display the image directly
    if (lazy === false && retainAspectRatio === false) {
        return (
            <NormalImage src={src} size={size} className={className} alt={alt || get(src, "alt", "")} cPosition={cPosition} showCaption={showCaption} />
        )
    }

    //Lazy load the image
    return (
        <LazyImage callBack={callBack} isLoad={isLoad} src={src} size={size} className={className} alt={alt || get(src, "alt", "")} sizer={retainAspectRatio} defaultSize={defaultSize} cPosition={cPosition} showCaption={showCaption} />
    )
}

export default Background;